import React from 'react';
import { useQuery } from '@apollo/react-hooks';

import { DefaultMeta } from 'src/components/AppShell/Head';
import { useDeps } from 'src/hooks/useDeps';
import { LoaderMinHeight } from 'src/components/Helpers/Skeleton';
import { cleanBreadcrumbs } from 'src/components/Breadcrumbs/breadcrumb.utils';
import { cleanCategories } from 'src/components/CategoryList/categoryListUtils';
import { CategoryLandingPage } from 'src/components/CategoryLandingPage/CategoryLandingPage';
import { ErrorOrOfflineConnected } from 'src/components/Error/ErrorOrOffline';
import { fireGTMEvent } from 'src/features/analytics/hooks/useGtm';
import {
    categoryList,
    categoryList_categoryList_children,
    categoryListVariables,
} from 'src/queries/__generated__/categoryList';

import categoryListQuery from '../../queries/categoryList.graphql';

import '../../scss/_root-component.scss';

type Props = {
    id: number;
    pathname: string;
};

export const CategoryRoot: React.FC<Props> = ({ id, pathname }) => {
    const { loading, error, data } = useQuery<categoryList, categoryListVariables>(categoryListQuery, {
        variables: { id: id },
        ssr: useDeps().env.SSR_GQL,
    });

    React.useEffect(() => {
        if (!error && !loading) fireGTMEvent({ event: 'pageview', pageType: 'Category Landing Page' });
    }, [error, loading]);

    if (error) return <ErrorOrOfflineConnected message={`Data fetch error ${error}`} />;

    /**
     * Loading handler
     */
    if (loading) {
        return <LoaderMinHeight />;
    }

    if (!data) return null;
    if (!data.categoryList) return null;
    if (!data.categoryList.children) return null;

    const { name, meta_title, meta_description, breadcrumbs, blog_ids } = data.categoryList;
    const categoryName = data.categoryList.name;
    const categoryFeaturedChildren = data.categoryList.children;
    const categoryDescription = data.categoryList.description;
    const categoryShortDescription = data.categoryList.short_description;

    const meta = {
        title: meta_title || name || 'unknown',
        description: meta_description || name || 'unknown',
    };

    // Clean up the blogIds from the dataset
    // Start with undefined
    let blogIds: string[] | undefined = undefined;

    // If the var isn't null, filter out any possible null values, whilst ensuring it with a custom type guard
    if (blog_ids !== null) {
        blogIds = blog_ids?.filter((item): item is string => item !== null);
    }

    return (
        <>
            <DefaultMeta data={meta} />
            <CategoryLandingPage
                name={categoryName!}
                description={categoryDescription}
                categoryShortDescription={categoryShortDescription}
                categories={cleanCategories(categoryFeaturedChildren as categoryList_categoryList_children[])}
                breadcrumbs={cleanBreadcrumbs(breadcrumbs)}
                pathname={pathname}
                blogPostIds={blogIds}
            />
        </>
    );
};

export default CategoryRoot;
